import React, { useContext, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useEntitlements } from '../contexts/EntitlementsContext';
import { useLanguage } from '../contexts/LanguageContext';
import { hasPaidAccess, hasRunningScoutAccess } from '../entitlements';
import { TrialIndicator } from '../opportunities/OpportunityComponents';
import UpgradeDialog from './UpgradeDialog';
import { clearUpgradeDraft, takeUpgradeIntent } from './upgradeIntent';

import ExperienceContext from './UpgradeContext';
export { useUpgradeExperience } from './UpgradeContext';

export function PlanControlSlot() {
  const experience = useContext(ExperienceContext);
  const { language } = useLanguage();
  if (!experience?.entitlements) return null;
  const { paid, trial, endsAt, openPlan } = experience;
  if (paid || !trial) return null;
  return <button type="button" className="js-plan-control is-trial" onClick={openPlan} aria-haspopup="dialog" aria-label={language === 'ar' ? 'عرض خطة Pro' : 'View Pro plan'}>
    <TrialIndicator access={{ trial, endsAt }} />
  </button>;
}

export function UpgradeExperienceProvider({ user, profile, onProfileSaved, children }) {
  const { entitlements } = useEntitlements();
  const location = useLocation();
  const navigate = useNavigate();
  const paid = hasPaidAccess(entitlements);
  const endsAt = Number(entitlements?.scout?.trialEndsAt || 0);
  const [now, setNow] = useState(Date.now);
  const [planOpen, setPlanOpen] = useState(false);
  const [expiryOpen, setExpiryOpen] = useState(false);
  const shown = useRef(new Set());
  const saveProfile = useRef(onProfileSaved);
  saveProfile.current = onProfileSaved;
  const trial = !paid && hasRunningScoutAccess(entitlements, now) && Boolean(entitlements?.scout?.trialActive);
  const expired = Boolean(entitlements && !paid && endsAt > 0 && endsAt <= now && !hasRunningScoutAccess(entitlements, now));
  const billing = new URLSearchParams(location.search).get('billing');

  useEffect(() => { setPlanOpen(false); setExpiryOpen(false); }, [user?.uid]);

  useEffect(() => {
    setNow(Date.now());
    if (endsAt <= Date.now()) return undefined;
    const timer = setTimeout(() => setNow(Date.now()), Math.min(endsAt - Date.now() + 150, 2147483647));
    return () => clearTimeout(timer);
  }, [endsAt, entitlements]);

  useEffect(() => {
    if (paid) { setPlanOpen(false); setExpiryOpen(false); }
    if (location.pathname === '/profile' && billing === 'cancelled') clearUpgradeDraft(user?.uid, 'return');
    if (!paid || location.pathname !== '/profile' || !['success', 'whish'].includes(billing)) return;
    if (new URLSearchParams(location.search).get('whishAttempt') === 'failed') return;
    const intent = takeUpgradeIntent(user?.uid);
    if (intent && intent.path !== '/profile') navigate(intent.path, { replace: true, state: intent.state });
  }, [paid, billing, location.pathname, location.search, user?.uid, navigate]);

  useEffect(() => {
    if (!expired || !user?.uid || billing) return undefined;
    const key = `jobseeqr:trial-expiry:${user.uid}:${endsAt}`;
    if (Number(profile?.upgradeExperience?.scoutTrialExpirySeenAt) === endsAt || shown.current.has(key)) return undefined;
    try { if (localStorage.getItem(key)) return undefined; } catch { /* The profile also remembers it. */ }
    const showWhenReady = () => {
      if (shown.current.has(key) || document.visibilityState === 'hidden') return;
      try { if (localStorage.getItem(key)) return; } catch { /* Profile/session fallback. */ }
      // Do not stack this offer over another modal or the onboarding tour.
      if (document.querySelector('dialog[open], [role="dialog"][aria-modal="true"]') || document.body.classList.contains('js-tutorial-open')) return;
      shown.current.add(key);
      try { localStorage.setItem(key, 'seen'); } catch { /* The current session still remembers it. */ }
      setExpiryOpen(true);
      Promise.resolve().then(() => saveProfile.current?.({ 'upgradeExperience/scoutTrialExpirySeenAt': endsAt })).catch(() => {});
    };
    const observer = new MutationObserver(showWhenReady);
    observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['open', 'class', 'aria-modal'] });
    const timer = setTimeout(showWhenReady, 0);
    document.addEventListener('visibilitychange', showWhenReady);
    return () => { observer.disconnect(); clearTimeout(timer); document.removeEventListener('visibilitychange', showWhenReady); };
  }, [expired, endsAt, user?.uid, profile?.upgradeExperience?.scoutTrialExpirySeenAt, billing]);

  return <ExperienceContext.Provider value={{ entitlements, paid, trial, endsAt, openPlan: () => setPlanOpen(true) }}>
    {children}
    {planOpen && !paid && <UpgradeDialog user={user} onClose={() => setPlanOpen(false)} />}
    {expiryOpen && !paid && <UpgradeDialog user={user} expiry onClose={() => setExpiryOpen(false)} destination={{ path: '/opportunities' }} />}
  </ExperienceContext.Provider>;
}
